import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Home2025DataflowCarousel } from '../../types/contentstack';
import { getAssetUrl } from '../../utils/contentstack';
import { mediaScreen } from '../../utils/mediaScreen';
import { ResponsiveContainer } from '../responsive';

export const DataflowCarousel: React.FC<DataflowProps> = ({ items }) => {
  const slides = (items || []).map((i) => {
    return {
      desktop: getAssetUrl(i?.image_desktopConnection),
      mobile: getAssetUrl(i?.image_mobileConnection),
    };
  });

  const SLIDE_MS = 6000;
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    if (!slides.length) return;

    const interval = setInterval(
      () => setIndex((i) => (i + 1) % slides.length),
      SLIDE_MS
    );

    return () => clearInterval(interval);
  }, [slides.length]);

  if (!slides.length) return null;

  return (
    <CarouselWrapper>
      <SlideStage>
        {slides.map((src, i) => (
          <SlideImage
            key={i}
            style={{
              opacity: i === index ? 1 : 0,
              zIndex: i === index ? 2 : 1,
            }}
            aria-hidden={i !== index}
          >
            <img
              src={src.desktop}
              className="desktop-only"
              alt="Dataflow visualization"
            />
            <img
              src={src.mobile}
              className="mobile-only"
              alt="Dataflow visualization"
            />
          </SlideImage>
        ))}
      </SlideStage>

      <Indicators>
        {slides.map((_, i) => (
          <Indicator key={i} active={i === index} duration={SLIDE_MS} />
        ))}
      </Indicators>
    </CarouselWrapper>
  );
};

const Indicator: React.FC<{ active: boolean; duration: number }> = ({
  active,
  duration,
}) => {
  return (
    <IndicatorBase aria-hidden="true">
      {active && <IndicatorFill duration={duration} />}
    </IndicatorBase>
  );
};

const CarouselWrapper = styled(ResponsiveContainer)`
  position: relative;
`;

const SlideStage = styled.div`
  width: 100%;
  margin: 0 auto;
  aspect-ratio: 16 / 9;
  position: relative;

  ${mediaScreen('xs', 'md')} {
    aspect-ratio: 9 / 11;
  }
`;

const SlideImage = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 1.2s ease-in-out;
  pointer-events: none;

  img {
    width: 100%;
    height: 100%;
  }

  .desktop-only {
    display: block;
  }

  .mobile-only {
    display: none;
  }

  ${mediaScreen('xs', 'md')} {
    .desktop-only {
      display: none;
    }

    .mobile-only {
      display: block;
    }
  }
`;

const Indicators = styled.div`
  margin: 48px auto 0;
  display: flex;
  justify-content: center;
  gap: 30px;
  max-width: 700px;

  ${mediaScreen('xs', 'md')} {
    gap: 32px;
    margin-top: 32px;
  }
`;

const IndicatorBase = styled.div`
  position: relative;
  width: 120px;
  height: 2px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;

  ${mediaScreen('xs', 'md')} {
    width: 70px;
  }
`;

const fillBar = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const IndicatorFill = styled.div<{ duration: number }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: ${(p) => p.theme.colors.teal};
  animation: ${fillBar} ${(p) => p.duration}ms ease forwards;
`;

interface DataflowProps
  extends Omit<Home2025DataflowCarousel, 'headline' | 'subheadline'> {}
